import React, { useState } from "react";
import styled from "styled-components";
import { Col, Row, Collapse } from "reactstrap";
import { RebelColors, RebelDangerButton, RebelIcon } from "../../../styles/rebel-experiences";
import { displayValue } from "../helper";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { calculateDateBasedAccommodation, hasDatePricing } from "../../../utils/date-pricing-utils";

/**
 * Property tab showing cost breakdown.
 * Uses date-based pricing for accommodation when available, otherwise falls back to basicViewPricing.
 * basicViewPricing is a map of category-id -> per-person price.
 */
const RebelBudgetPropertyTab = ({ villaId, villa, categories, config, tripLength, currencySymbol, onRemove, viewOnly }) => {
    const [showBreakdown, setShowBreakdown] = useState(false);
    const [showMeals, setShowMeals] = useState(false);
    const [showTransport, setShowTransport] = useState(false);
    const [showActivities, setShowActivities] = useState(false);

    if (!villa) {
        return <LoadingText>Loading property data...</LoadingText>;
    }

    const pricing = villa.basicViewPricing || {};
    const attendees = config.attendees || 1;
    const hasDates = config.startDate && config.endDate;
    const villaHasDatePricing = hasDatePricing(villa);

    // Extract currency code from currency string (e.g., "USD$" -> "USD")
    const currencyCode = config.currency ? config.currency.slice(0, 3) : "USD";

    // Compute accommodation breakdown from date pricing grid
    // Uses villaRentalFee as base nightly rate, with datePricing overrides per date
    const hasBaseRate = villa.villaRentalFee && parseFloat(villa.villaRentalFee) > 0;
    const accomBreakdown = (hasDates && (villaHasDatePricing || hasBaseRate))
        ? calculateDateBasedAccommodation(config.startDate, config.endDate, villa)
        : null;

    // Get category name from id
    const getCategoryName = (catId) => {
        const cat = categories.find(c => c.id === catId || c._docId === catId);
        return cat?.name || cat?.title || catId;
    };

    // Check if a category is accommodation
    const isAccommodation = (catName) => {
        const name = (catName || "").toLowerCase();
        return name.includes("accommodation") || name.includes("lodging");
    };

    // Map category names to icons
    const getCategoryIcon = (catName) => {
        const name = (catName || "").toLowerCase();
        if (name.includes("accommodation") || name.includes("lodging")) return 'bed';
        if (name.includes("flight") || name.includes("air")) return 'plane';
        if (name.includes("activity") || name.includes("activities") || name.includes("excursion")) return 'running';
        if (name.includes("transport") || name.includes("ground") || name.includes("transfer")) return 'car';
        if (name.includes("meal") || name.includes("food") || name.includes("beverage") || name.includes("catering") ||
            name.includes("breakfast") || name.includes("lunch") || name.includes("dinner")) return 'utensils';
        if (name.includes("meeting") || name.includes("conference") || name.includes("av") || name.includes("setup")) return 'chalkboard-teacher';
        if (name.includes("misc") || name.includes("other") || name.includes("additional")) return 'plus-circle';
        if (name.includes("contingency") || name.includes("buffer")) return 'shield-alt';
        if (name.includes("coffee") || name.includes("break")) return 'coffee';
        return 'tag';
    };

    // Determine if a category should be multiplied by trip length
    const isDayBased = (catId, catName) => {
        const name = (catName || catId || "").toLowerCase();
        return name.includes("accommodation") || name.includes("lodging") ||
               name.includes("meal") || name.includes("food") || name.includes("beverage") ||
               name.includes("breakfast") || name.includes("lunch") || name.includes("dinner");
    };

    // Category grouping helpers
    const isMealCategory = (catName) => {
        const name = (catName || "").toLowerCase();
        return name.includes("meal") || name.includes("food") || name.includes("beverage") ||
               name.includes("breakfast") || name.includes("lunch") || name.includes("dinner") ||
               name.includes("coffee") || name.includes("break") || name.includes("catering");
    };

    const isFlightCategory = (catName) => {
        const name = (catName || "").toLowerCase();
        return name.includes("flight") || name.includes("air");
    };

    const isTransportCategory = (catName) => {
        const name = (catName || "").toLowerCase();
        return name.includes("transport") || name.includes("transfer") || name.includes("ground");
    };

    const isActivityCategory = (catName) => {
        const name = (catName || "").toLowerCase();
        return name.includes("activity") || name.includes("activities") || name.includes("excursion") ||
               name.includes("learning") || name.includes("team building") || name.includes("team-building") ||
               name.includes("meeting") || name.includes("free time") || name.includes("evening");
    };

    // Clean up display names (e.g., "Team-Building" → "Team Building")
    const cleanDisplayName = (name) => {
        return (name || "").replace(/Team-Building/gi, "Team Building")
                           .replace(/team-building/gi, "Team Building");
    };

    // Build cost rows — accommodation comes from date pricing grid, everything else from basicViewPricing
    const costRows = [];

    // Accommodation row from date pricing grid (not basicViewPricing)
    if (accomBreakdown && accomBreakdown.total > 0) {
        costRows.push({
            catId: '_accommodation', catName: 'Accommodation',
            perPerson: accomBreakdown.subtotal / (accomBreakdown.nightCount || 1),
            nights: accomBreakdown.nightCount, dayBased: true,
            totalPerPerson: accomBreakdown.total / attendees, totalForTrip: accomBreakdown.total,
            icon: 'bed', isDateBased: true, isMeal: false, isFlight: false, isTransport: false, isActivity: false
        });
    } else if (hasBaseRate && tripLength > 0) {
        // Fallback: show base rate × nights when no dates are set
        const baseNightly = parseFloat(villa.villaRentalFee) || 0;
        const cleaning = parseFloat(villa.cleaningFee) || 0;
        const accomTotal = (baseNightly * tripLength) + cleaning;
        costRows.push({
            catId: '_accommodation', catName: 'Accommodation',
            perPerson: baseNightly, nights: tripLength, dayBased: true,
            totalPerPerson: accomTotal / attendees, totalForTrip: accomTotal,
            icon: 'bed', isDateBased: false, isMeal: false, isFlight: false, isTransport: false, isActivity: false
        });
    }

    // Other cost rows from basicViewPricing (skip any accommodation entries)
    Object.entries(pricing)
        .filter(([, price]) => parseFloat(price) > 0)
        .forEach(([catId, price]) => {
            const perPerson = parseFloat(price) || 0;
            const rawCatName = getCategoryName(catId);

            // Skip accommodation — already handled above from date pricing grid
            if (isAccommodation(rawCatName)) return;

            const catName = cleanDisplayName(rawCatName);
            const dayBased = isDayBased(catId, rawCatName);
            const icon = getCategoryIcon(rawCatName);
            const isMeal = isMealCategory(rawCatName);
            const isFlight = isFlightCategory(rawCatName);
            const isTransport = isTransportCategory(rawCatName);
            const isActivity = isActivityCategory(rawCatName);

            const nights = dayBased ? (tripLength || 1) : 1;
            const totalPerPerson = perPerson * nights;
            const totalForTrip = totalPerPerson * attendees;
            costRows.push({ catId, catName, perPerson, nights, dayBased, totalPerPerson, totalForTrip, icon, isDateBased: false, isMeal, isFlight, isTransport, isActivity });
        });

    // Split rows into groups
    const ungroupedRows = costRows.filter(row => !row.isMeal && !row.isTransport && !row.isActivity);
    const mealRows = costRows.filter(row => row.isMeal);
    const transportRows = costRows.filter(row => row.isTransport);
    const activityRows = costRows.filter(row => row.isActivity);

    const sumPP = (rows) => rows.reduce((s, r) => s + r.totalPerPerson, 0);
    const sumTrip = (rows) => rows.reduce((s, r) => s + r.totalForTrip, 0);

    const mealsTotalPerPerson = sumPP(mealRows);
    const mealsTotalTrip = sumTrip(mealRows);
    const transportTotalPerPerson = sumPP(transportRows);
    const transportTotalTrip = sumTrip(transportRows);
    const activityTotalPerPerson = sumPP(activityRows);
    const activityTotalTrip = sumTrip(activityRows);

    // === Fee calculation ===
    // Totals split by flight vs non-flight (flights excluded from all % fee calculations)
    const flightTotalTrip = costRows.filter(row => row.isFlight).reduce((sum, row) => sum + row.totalForTrip, 0);
    const flightTotalPerPerson = costRows.filter(row => row.isFlight).reduce((sum, row) => sum + row.totalPerPerson, 0);
    const nonFlightTotalTrip = costRows.filter(row => !row.isFlight).reduce((sum, row) => sum + row.totalForTrip, 0);
    const nonFlightTotalPerPerson = costRows.filter(row => !row.isFlight).reduce((sum, row) => sum + row.totalPerPerson, 0);

    // "Don't include" flags from villa settings
    const excludeContingency = villa.excludeContingency === true;
    const excludeAdminFee = villa.excludeAdminFee === true;
    const excludePlanningFee = villa.excludePlanningFee === true;

    // Contingency — defaults to 5%, applied to non-flight costs
    const contingencyPercent = !excludeContingency
        ? (villa.contingencyPercent !== undefined && villa.contingencyPercent !== null && villa.contingencyPercent !== '' && villa.contingencyPercent !== 0
            ? parseFloat(villa.contingencyPercent) : 5)
        : 0;
    const contingencyAmount = contingencyPercent > 0 ? nonFlightTotalTrip * (contingencyPercent / 100) : 0;
    const contingencyPerPerson = contingencyPercent > 0 ? nonFlightTotalPerPerson * (contingencyPercent / 100) : 0;

    // Admin fee — defaults to 5%, % excludes flights, applied on (non-flight + contingency)
    const adminFeePercent = !excludeAdminFee
        ? (villa.adminFeePercent !== undefined && villa.adminFeePercent !== null && villa.adminFeePercent !== '' && villa.adminFeePercent !== 0
            ? parseFloat(villa.adminFeePercent) : 5)
        : 0;
    const adminFeeFixedTotal = villa.adminFeeFixed ? parseFloat(villa.adminFeeFixed) : 0;
    const adminFeeFixedPP = villa.adminFeeFixedPP ? parseFloat(villa.adminFeeFixedPP) : 0;
    let adminFeeAmount = 0;
    let adminFeeLabel = '';
    if (!excludeAdminFee) {
        if (adminFeePercent > 0) {
            adminFeeAmount = (nonFlightTotalTrip + contingencyAmount) * (adminFeePercent / 100);
            adminFeeLabel = `(${adminFeePercent}%)`;
        } else if (adminFeeFixedPP > 0) {
            adminFeeAmount = adminFeeFixedPP * attendees;
            adminFeeLabel = `(${currencySymbol}${displayValue(adminFeeFixedPP)} ${currencyCode}/person)`;
        } else if (adminFeeFixedTotal > 0) {
            adminFeeAmount = adminFeeFixedTotal;
            adminFeeLabel = '(flat)';
        }
    }

    // Planning fee — defaults to 5%, % excludes flights, applied on (non-flight + contingency)
    const planningFeePercent = !excludePlanningFee
        ? (villa.planningFeePercent !== undefined && villa.planningFeePercent !== null && villa.planningFeePercent !== '' && villa.planningFeePercent !== 0
            ? parseFloat(villa.planningFeePercent) : 5)
        : 0;
    const planningFeeFixedTotal = villa.planningFeeFixed ? parseFloat(villa.planningFeeFixed) : 0;
    const planningFeeFixedPP = villa.planningFeeFixedPP ? parseFloat(villa.planningFeeFixedPP) : 0;
    let planningFeeAmount = 0;
    let planningFeeLabel = '';
    if (!excludePlanningFee) {
        if (planningFeePercent > 0) {
            planningFeeAmount = (nonFlightTotalTrip + contingencyAmount) * (planningFeePercent / 100);
            planningFeeLabel = `(${planningFeePercent}%)`;
        } else if (planningFeeFixedPP > 0) {
            planningFeeAmount = planningFeeFixedPP * attendees;
            planningFeeLabel = `(${currencySymbol}${displayValue(planningFeeFixedPP)} ${currencyCode}/person)`;
        } else if (planningFeeFixedTotal > 0) {
            planningFeeAmount = planningFeeFixedTotal;
            planningFeeLabel = '(flat)';
        }
    }

    // Additional extra fees from villa data
    const extraFees = villa.extraFees || [];

    // Subtotal = all cost rows + contingency (contingency is BEFORE subtotal)
    const rawSubtotalTrip = costRows.reduce((sum, row) => sum + row.totalForTrip, 0);
    const rawSubtotalPerPerson = costRows.reduce((sum, row) => sum + row.totalPerPerson, 0);
    const subtotalTrip = rawSubtotalTrip + contingencyAmount;
    const subtotalPerPerson = rawSubtotalPerPerson + contingencyPerPerson;

    // Grand total = subtotal + admin + planning + extras
    const grandTotal = subtotalTrip + adminFeeAmount + planningFeeAmount +
        extraFees.reduce((sum, fee) => sum + (parseFloat(fee.amount) || 0), 0);
    const grandTotalPerPerson = grandTotal / attendees;

    // Collect all warnings for the Alerts section
    const allWarnings = accomBreakdown ? accomBreakdown.warnings : [];
    const hasAlerts = allWarnings.length > 0;

    return (
        <div>
            {/* Property header */}
            <PropertyHeader>
                <PropertyName>{villa.villaName || villa.name || "Property"}</PropertyName>
                {villa.location && <PropertyLocation>{villa.location}</PropertyLocation>}
            </PropertyHeader>

            {/* Villa details */}
            {(villa.numberOfBedrooms || villa.distanceFromAirport || villa.nearestCity || villa.distanceToCity || villa.villaURL) && (
                <VillaDetailsContainer>
                    {villa.numberOfBedrooms > 0 && <VillaDetail><strong>Bedrooms:</strong> {villa.numberOfBedrooms}</VillaDetail>}
                    {villa.distanceFromAirport && <VillaDetail><strong>Distance from Airport:</strong> {villa.distanceFromAirport}</VillaDetail>}
                    {villa.nearestCity && <VillaDetail><strong>Nearest City:</strong> {villa.nearestCity}</VillaDetail>}
                    {villa.distanceToCity && <VillaDetail><strong>Distance to City:</strong> {villa.distanceToCity}</VillaDetail>}
                    {villa.villaURL && (
                        <ViewPropertyButton
                            href={villa.villaURL.startsWith('http') ? villa.villaURL : `https://${villa.villaURL}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            View Property
                        </ViewPropertyButton>
                    )}
                </VillaDetailsContainer>
            )}

            {/* Property notes */}
            {villa.notes && (
                <NotesContainer>
                    <NotesLabel>
                        <FontAwesomeIcon icon={['fas', 'sticky-note']} style={{ marginRight: 8, color: RebelColors.gold }} />
                        Property Notes
                    </NotesLabel>
                    <NotesText>{villa.notes}</NotesText>
                </NotesContainer>
            )}

            {/* === ALERTS SECTION (#15-16) - Prominent warnings for blackout/call-to-book === */}
            {hasAlerts && (
                <AlertsSection>
                    <AlertsSectionHeader>
                        <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} style={{ marginRight: 8 }} />
                        Important Notices
                    </AlertsSectionHeader>
                    {allWarnings.map((warning, i) => (
                        <AlertItem key={i} $type={warning.type}>
                            <AlertIcon $type={warning.type}>
                                <FontAwesomeIcon icon={['fas', warning.type === 'blackout' ? 'times' : warning.type === 'callToBook' ? 'exclamation-circle' : 'exclamation-triangle']} />
                            </AlertIcon>
                            <AlertContent>
                                <AlertTitle $type={warning.type}>
                                    {warning.type === 'blackout' ? 'Blackout Date' :
                                     warning.type === 'callToBook' ? 'Limited Availability' :
                                     warning.type === 'minNights' ? 'Minimum Stay' :
                                     warning.type === 'maxNights' ? 'Maximum Stay' :
                                     warning.type === 'checkInDay' ? 'Check-in Restriction' :
                                     warning.type === 'checkOutDay' ? 'Check-out Restriction' : 'Notice'}
                                </AlertTitle>
                                <AlertMessage>{warning.message}</AlertMessage>
                                {/* Show reason/note for the date if available (#16) */}
                                {warning.date && accomBreakdown && (() => {
                                    const nightEntry = accomBreakdown.nightlyBreakdown.find(n => n.date === warning.date);
                                    if (nightEntry && nightEntry.note) {
                                        return (
                                            <AlertReason $type={warning.type}>
                                                <FontAwesomeIcon icon={['fas', 'info-circle']} style={{ marginRight: 6, flexShrink: 0 }} />
                                                {nightEntry.note}
                                            </AlertReason>
                                        );
                                    }
                                    return null;
                                })()}
                            </AlertContent>
                        </AlertItem>
                    ))}
                </AlertsSection>
            )}

            {/* Discount badge */}
            {accomBreakdown && accomBreakdown.discount.applied && (
                <DiscountBadge>
                    <FontAwesomeIcon icon={['fas', 'tag']} style={{ marginRight: 6 }} />
                    Discount applied: {accomBreakdown.discount.description} (-{currencySymbol}{displayValue(accomBreakdown.discount.amount)} {currencyCode})
                </DiscountBadge>
            )}

            {/* Cost summary header */}
            <SummaryHeader>
                <SummaryItem>
                    <SummaryLabel>Per Attendee Cost</SummaryLabel>
                    <SummaryValue>{currencySymbol}{displayValue(grandTotalPerPerson)} {currencyCode}</SummaryValue>
                </SummaryItem>
                <SummaryItem>
                    <SummaryLabel>Total Cost ({attendees} attendees)</SummaryLabel>
                    <SummaryValue>{currencySymbol}{displayValue(grandTotal)} {currencyCode}</SummaryValue>
                </SummaryItem>
            </SummaryHeader>

            {/* Cost breakdown table */}
            <TableTitle>Trip Breakdown</TableTitle>
            <CostTableContainer>
                {/* Header */}
                <HeaderRow>
                    <Col lg="1"></Col>
                    <Col>
                        <BoldLabel>Category</BoldLabel>
                    </Col>
                    <Col lg="2">
                        <BoldLabel>Per Attendee ({currencyCode})</BoldLabel>
                    </Col>
                    <Col lg="1"></Col>
                    <Col lg="2">
                        <BoldLabel>Subtotal ({currencyCode})</BoldLabel>
                    </Col>
                </HeaderRow>

                {/* Ungrouped cost rows (accommodation, flights, misc, etc.) */}
                {ungroupedRows.map(row => (
                    <React.Fragment key={row.catId}>
                        <CostRow>
                            <IconCol lg="1">
                                <CategoryIcon icon={['fas', row.icon]} />
                            </IconCol>
                            <Col>
                                <CategoryName>{row.catName}</CategoryName>
                                <CostDescription>
                                    {row.isDateBased ? (
                                        <>
                                            {accomBreakdown.nightCount} night{accomBreakdown.nightCount !== 1 ? 's' : ''}
                                            {accomBreakdown.cleaningFee > 0 && ` + ${currencySymbol}${displayValue(accomBreakdown.cleaningFee)} ${currencyCode} cleaning`}
                                            <BreakdownToggle onClick={() => setShowBreakdown(!showBreakdown)}>
                                                {showBreakdown ? 'Hide' : 'View'} nightly rates
                                                <FontAwesomeIcon icon={['fas', showBreakdown ? 'chevron-up' : 'chevron-down']} style={{ marginLeft: 4, fontSize: 10 }} />
                                            </BreakdownToggle>
                                        </>
                                    ) : (
                                        <>
                                            {currencySymbol}{displayValue(row.perPerson)} {currencyCode}/person
                                            {row.dayBased && tripLength > 0 && ` × ${row.nights} night${row.nights !== 1 ? 's' : ''}`}
                                        </>
                                    )}
                                </CostDescription>
                            </Col>
                            <Col lg="2">
                                <CostAmount>{currencySymbol}{displayValue(row.totalPerPerson)} {currencyCode}</CostAmount>
                            </Col>
                            <MultiplierCol lg="1">
                                <MultiplierText>× {attendees}</MultiplierText>
                            </MultiplierCol>
                            <Col lg="2">
                                <CostAmount>{currencySymbol}{displayValue(row.totalForTrip)} {currencyCode}</CostAmount>
                            </Col>
                        </CostRow>

                        {/* Nightly breakdown for date-based accommodation */}
                        {row.isDateBased && accomBreakdown && (
                            <Collapse isOpen={showBreakdown}>
                                <NightlyBreakdownContainer>
                                    {accomBreakdown.nightlyBreakdown.map(night => (
                                        <NightlyRow key={night.date} $isBlackout={night.isBlackout} $isCallToBook={night.isCallToBook}>
                                            <NightDate>
                                                {new Date(night.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                            </NightDate>
                                            <NightRate $isOverride={night.isOverride}>
                                                {currencySymbol}{displayValue(night.rate)} {currencyCode}
                                                {night.isOverride && <OverrideIndicator>custom</OverrideIndicator>}
                                            </NightRate>
                                            {night.isBlackout && <NightFlag $type="blackout">Blackout</NightFlag>}
                                            {night.isCallToBook && <NightFlag $type="callToBook">Limited Availability</NightFlag>}
                                            {night.note && <NightNote>{night.note}</NightNote>}
                                        </NightlyRow>
                                    ))}
                                    {accomBreakdown.cleaningFee > 0 && (
                                        <NightlyRow>
                                            <NightDate>Cleaning Fee</NightDate>
                                            <NightRate>{currencySymbol}{displayValue(accomBreakdown.cleaningFee)} {currencyCode}</NightRate>
                                        </NightlyRow>
                                    )}
                                    {accomBreakdown.discount.applied && (
                                        <NightlyRow>
                                            <NightDate style={{ color: '#10b981' }}>Discount</NightDate>
                                            <NightRate style={{ color: '#10b981' }}>-{currencySymbol}{displayValue(accomBreakdown.discount.amount)} {currencyCode}</NightRate>
                                        </NightlyRow>
                                    )}
                                </NightlyBreakdownContainer>
                            </Collapse>
                        )}
                    </React.Fragment>
                ))}

                {/* Activities group — collapsible */}
                {activityRows.length > 0 && (
                    <React.Fragment>
                        <GroupHeader onClick={() => setShowActivities(!showActivities)}>
                            <IconCol lg="1">
                                <CategoryIcon icon={['fas', 'running']} />
                            </IconCol>
                            <Col>
                                <GroupLabel>
                                    Activities
                                    <GroupToggle>
                                        {activityRows.length} item{activityRows.length !== 1 ? 's' : ''}
                                        <FontAwesomeIcon icon={['fas', showActivities ? 'chevron-up' : 'chevron-down']} style={{ marginLeft: 6, fontSize: 10 }} />
                                    </GroupToggle>
                                </GroupLabel>
                            </Col>
                            <Col lg="2">
                                <CostAmount>{currencySymbol}{displayValue(activityTotalPerPerson)} {currencyCode}</CostAmount>
                            </Col>
                            <MultiplierCol lg="1">
                                <MultiplierText>× {attendees}</MultiplierText>
                            </MultiplierCol>
                            <Col lg="2">
                                <CostAmount>{currencySymbol}{displayValue(activityTotalTrip)} {currencyCode}</CostAmount>
                            </Col>
                        </GroupHeader>
                        <Collapse isOpen={showActivities}>
                            {activityRows.map(row => (
                                <GroupSubRow key={row.catId}>
                                    <MealIndentCol lg="1"></MealIndentCol>
                                    <Col>
                                        <MealSubName>{row.catName}</MealSubName>
                                        <MealSubDescription>
                                            {currencySymbol}{displayValue(row.perPerson)} {currencyCode}/person
                                        </MealSubDescription>
                                    </Col>
                                    <Col lg="2">
                                        <MealSubAmount>{currencySymbol}{displayValue(row.totalPerPerson)} {currencyCode}</MealSubAmount>
                                    </Col>
                                    <MultiplierCol lg="1">
                                        <MultiplierText>× {attendees}</MultiplierText>
                                    </MultiplierCol>
                                    <Col lg="2">
                                        <MealSubAmount>{currencySymbol}{displayValue(row.totalForTrip)} {currencyCode}</MealSubAmount>
                                    </Col>
                                </GroupSubRow>
                            ))}
                        </Collapse>
                    </React.Fragment>
                )}

                {/* Transportation group — collapsible */}
                {transportRows.length > 0 && (
                    <React.Fragment>
                        <GroupHeader onClick={() => setShowTransport(!showTransport)}>
                            <IconCol lg="1">
                                <CategoryIcon icon={['fas', 'car']} />
                            </IconCol>
                            <Col>
                                <GroupLabel>
                                    Transportation
                                    <GroupToggle>
                                        {transportRows.length} item{transportRows.length !== 1 ? 's' : ''}
                                        <FontAwesomeIcon icon={['fas', showTransport ? 'chevron-up' : 'chevron-down']} style={{ marginLeft: 6, fontSize: 10 }} />
                                    </GroupToggle>
                                </GroupLabel>
                            </Col>
                            <Col lg="2">
                                <CostAmount>{currencySymbol}{displayValue(transportTotalPerPerson)} {currencyCode}</CostAmount>
                            </Col>
                            <MultiplierCol lg="1">
                                <MultiplierText>× {attendees}</MultiplierText>
                            </MultiplierCol>
                            <Col lg="2">
                                <CostAmount>{currencySymbol}{displayValue(transportTotalTrip)} {currencyCode}</CostAmount>
                            </Col>
                        </GroupHeader>
                        <Collapse isOpen={showTransport}>
                            {transportRows.map(row => (
                                <GroupSubRow key={row.catId}>
                                    <MealIndentCol lg="1"></MealIndentCol>
                                    <Col>
                                        <MealSubName>{row.catName}</MealSubName>
                                        <MealSubDescription>
                                            {currencySymbol}{displayValue(row.perPerson)} {currencyCode}/person
                                        </MealSubDescription>
                                    </Col>
                                    <Col lg="2">
                                        <MealSubAmount>{currencySymbol}{displayValue(row.totalPerPerson)} {currencyCode}</MealSubAmount>
                                    </Col>
                                    <MultiplierCol lg="1">
                                        <MultiplierText>× {attendees}</MultiplierText>
                                    </MultiplierCol>
                                    <Col lg="2">
                                        <MealSubAmount>{currencySymbol}{displayValue(row.totalForTrip)} {currencyCode}</MealSubAmount>
                                    </Col>
                                </GroupSubRow>
                            ))}
                        </Collapse>
                    </React.Fragment>
                )}

                {/* Meals group — collapsible */}
                {mealRows.length > 0 && (
                    <React.Fragment>
                        <GroupHeader onClick={() => setShowMeals(!showMeals)}>
                            <IconCol lg="1">
                                <CategoryIcon icon={['fas', 'utensils']} />
                            </IconCol>
                            <Col>
                                <GroupLabel>
                                    Meals
                                    <GroupToggle>
                                        {mealRows.length} item{mealRows.length !== 1 ? 's' : ''}
                                        <FontAwesomeIcon icon={['fas', showMeals ? 'chevron-up' : 'chevron-down']} style={{ marginLeft: 6, fontSize: 10 }} />
                                    </GroupToggle>
                                </GroupLabel>
                            </Col>
                            <Col lg="2">
                                <CostAmount>{currencySymbol}{displayValue(mealsTotalPerPerson)} {currencyCode}</CostAmount>
                            </Col>
                            <MultiplierCol lg="1">
                                <MultiplierText>× {attendees}</MultiplierText>
                            </MultiplierCol>
                            <Col lg="2">
                                <CostAmount>{currencySymbol}{displayValue(mealsTotalTrip)} {currencyCode}</CostAmount>
                            </Col>
                        </GroupHeader>
                        <Collapse isOpen={showMeals}>
                            {mealRows.map(row => (
                                <GroupSubRow key={row.catId}>
                                    <MealIndentCol lg="1"></MealIndentCol>
                                    <Col>
                                        <MealSubName>{row.catName}</MealSubName>
                                        <MealSubDescription>
                                            {currencySymbol}{displayValue(row.perPerson)} {currencyCode}/person
                                            {row.dayBased && tripLength > 0 && ` × ${row.nights} night${row.nights !== 1 ? 's' : ''}`}
                                        </MealSubDescription>
                                    </Col>
                                    <Col lg="2">
                                        <MealSubAmount>{currencySymbol}{displayValue(row.totalPerPerson)} {currencyCode}</MealSubAmount>
                                    </Col>
                                    <MultiplierCol lg="1">
                                        <MultiplierText>× {attendees}</MultiplierText>
                                    </MultiplierCol>
                                    <Col lg="2">
                                        <MealSubAmount>{currencySymbol}{displayValue(row.totalForTrip)} {currencyCode}</MealSubAmount>
                                    </Col>
                                </GroupSubRow>
                            ))}
                        </Collapse>
                    </React.Fragment>
                )}

                {costRows.length === 0 && (
                    <EmptyRow>
                        <Col>
                            <EmptyText>No pricing data available for this property.</EmptyText>
                        </Col>
                    </EmptyRow>
                )}

                {/* Fee rows and subtotal */}
                {costRows.length > 0 && (
                    <>
                        {/* Contingency row — appears BEFORE the subtotal */}
                        {contingencyAmount > 0 && (
                            <FeeRow>
                                <IconCol lg="1">
                                    <FeeIcon icon={['fas', 'shield-alt']} />
                                </IconCol>
                                <Col>
                                    <FeeName>Contingency ({contingencyPercent}%)</FeeName>
                                </Col>
                                <Col lg="2">
                                    <FeeAmount>{currencySymbol}{displayValue(contingencyPerPerson)} {currencyCode}</FeeAmount>
                                </Col>
                                <MultiplierCol lg="1">
                                    <MultiplierText>× {attendees}</MultiplierText>
                                </MultiplierCol>
                                <Col lg="2">
                                    <FeeAmount>{currencySymbol}{displayValue(contingencyAmount)} {currencyCode}</FeeAmount>
                                </Col>
                            </FeeRow>
                        )}

                        {/* Subtotal line (includes contingency) */}
                        <SeparatorRow>
                            <Col><SeparatorLine /></Col>
                        </SeparatorRow>

                        <SubtotalRow>
                            <IconCol lg="1">
                                <CategoryIcon icon={['fas', 'calculator']} />
                            </IconCol>
                            <Col>
                                <SubtotalLabel>Subtotal</SubtotalLabel>
                            </Col>
                            <Col lg="2">
                                <SubtotalAmount>{currencySymbol}{displayValue(subtotalPerPerson)} {currencyCode}</SubtotalAmount>
                            </Col>
                            <MultiplierCol lg="1">
                                <MultiplierText>× {attendees}</MultiplierText>
                            </MultiplierCol>
                            <Col lg="2">
                                <SubtotalAmount>{currencySymbol}{displayValue(subtotalTrip)} {currencyCode}</SubtotalAmount>
                            </Col>
                        </SubtotalRow>

                        {/* Admin/Management fee row */}
                        {adminFeeAmount > 0 && (
                            <FeeRow>
                                <IconCol lg="1">
                                    <FeeIcon icon={['fas', 'cogs']} />
                                </IconCol>
                                <Col>
                                    <FeeName>Administrative & Management Fee {adminFeeLabel}</FeeName>
                                </Col>
                                <Col lg="2">
                                    <FeeAmount>{currencySymbol}{displayValue(adminFeeAmount / attendees)} {currencyCode}</FeeAmount>
                                </Col>
                                <MultiplierCol lg="1"></MultiplierCol>
                                <Col lg="2">
                                    <FeeAmount>{currencySymbol}{displayValue(adminFeeAmount)} {currencyCode}</FeeAmount>
                                </Col>
                            </FeeRow>
                        )}

                        {/* Planning fee row */}
                        {planningFeeAmount > 0 && (
                            <FeeRow>
                                <IconCol lg="1">
                                    <FeeIcon icon={['fas', 'clipboard']} />
                                </IconCol>
                                <Col>
                                    <FeeName>Planning Fee {planningFeeLabel}</FeeName>
                                </Col>
                                <Col lg="2">
                                    <FeeAmount>{currencySymbol}{displayValue(planningFeeAmount / attendees)} {currencyCode}</FeeAmount>
                                </Col>
                                <MultiplierCol lg="1"></MultiplierCol>
                                <Col lg="2">
                                    <FeeAmount>{currencySymbol}{displayValue(planningFeeAmount)} {currencyCode}</FeeAmount>
                                </Col>
                            </FeeRow>
                        )}

                        {/* Extra fees from villa data (#3) */}
                        {extraFees.map((fee, idx) => (
                            <FeeRow key={`extra-${idx}`}>
                                <IconCol lg="1">
                                    <FeeIcon icon={['fas', 'plus-circle']} />
                                </IconCol>
                                <Col>
                                    <FeeName>{fee.label || 'Additional Fee'}</FeeName>
                                </Col>
                                <Col lg="2">
                                    <FeeAmount>{currencySymbol}{displayValue((parseFloat(fee.amount) || 0) / attendees)} {currencyCode}</FeeAmount>
                                </Col>
                                <MultiplierCol lg="1"></MultiplierCol>
                                <Col lg="2">
                                    <FeeAmount>{currencySymbol}{displayValue(parseFloat(fee.amount) || 0)} {currencyCode}</FeeAmount>
                                </Col>
                            </FeeRow>
                        ))}

                        {/* Grand Total */}
                        <SeparatorRow>
                            <Col><GrandSeparatorLine /></Col>
                        </SeparatorRow>

                        <TotalRow>
                            <IconCol lg="1">
                                <TotalIcon icon={['fas', 'calculator']} />
                            </IconCol>
                            <Col>
                                <TotalLabel>Estimated Total</TotalLabel>
                            </Col>
                            <Col lg="2">
                                <TotalAmount>{currencySymbol}{displayValue(grandTotalPerPerson)} {currencyCode}</TotalAmount>
                            </Col>
                            <MultiplierCol lg="1">
                                <MultiplierText>× {attendees}</MultiplierText>
                            </MultiplierCol>
                            <Col lg="2">
                                <TotalAmount>{currencySymbol}{displayValue(grandTotal)} {currencyCode}</TotalAmount>
                            </Col>
                        </TotalRow>
                    </>
                )}
            </CostTableContainer>

            {/* Remove property button */}
            {!viewOnly && (
                <RemoveRow>
                    <RebelDangerButton onClick={onRemove}>
                        Remove Property
                    </RebelDangerButton>
                </RemoveRow>
            )}
        </div>
    );
};

export default RebelBudgetPropertyTab;

// Calculate total cost for a villa (used by summary)
export const calculateVillaTotalPerPerson = (villa, categories, tripLength, config) => {
    if (!villa) return 0;

    const pricing = villa.basicViewPricing || {};
    const attendees = config?.attendees || 1;

    const getCategoryName = (catId) => {
        const cat = categories.find(c => c.id === catId || c._docId === catId);
        return cat?.name || cat?.title || catId;
    };

    const isDayBased = (catId, catName) => {
        const name = (catName || catId || "").toLowerCase();
        return name.includes("meal") || name.includes("food") || name.includes("beverage") ||
               name.includes("breakfast") || name.includes("lunch") || name.includes("dinner");
    };

    const isAccom = (catName) => {
        const name = (catName || "").toLowerCase();
        return name.includes("accommodation") || name.includes("lodging");
    };

    const isFlightCat = (catName) => {
        const name = (catName || "").toLowerCase();
        return name.includes("flight") || name.includes("air");
    };

    // Accommodation from date pricing grid
    const hasDates = config && config.startDate && config.endDate;
    const villaHasDatePricing = hasDatePricing(villa);
    const hasBaseRate = villa.villaRentalFee && parseFloat(villa.villaRentalFee) > 0;

    let total = 0;
    let flightTotal = 0;

    if (hasDates && (villaHasDatePricing || hasBaseRate)) {
        const breakdown = calculateDateBasedAccommodation(config.startDate, config.endDate, villa);
        // Accommodation is a flat fee (not per-person), so divide by attendees for per-person
        total += breakdown.total / attendees;
    } else if (hasBaseRate && tripLength > 0) {
        const baseNightly = parseFloat(villa.villaRentalFee) || 0;
        const cleaning = parseFloat(villa.cleaningFee) || 0;
        total += ((baseNightly * tripLength) + cleaning) / attendees;
    }

    // Other costs from basicViewPricing (skip accommodation)
    Object.entries(pricing).forEach(([catId, price]) => {
        const perPerson = parseFloat(price) || 0;
        if (perPerson > 0) {
            const catName = getCategoryName(catId);
            if (isAccom(catName)) return; // Skip — handled above

            const nights = isDayBased(catId, catName) ? (tripLength || 1) : 1;
            const rowTotal = perPerson * nights;
            total += rowTotal;
            if (isFlightCat(catName)) {
                flightTotal += rowTotal;
            }
        }
    });

    // Non-flight total for fee calculations (per person)
    const nonFlightTotal = total - flightTotal;

    // Contingency — defaults to 5%, excludes flights
    const excludeContingency = villa.excludeContingency === true;
    const contingencyPercent = !excludeContingency
        ? (villa.contingencyPercent !== undefined && villa.contingencyPercent !== null && villa.contingencyPercent !== '' && villa.contingencyPercent !== 0
            ? parseFloat(villa.contingencyPercent) : 5)
        : 0;
    const contingencyAmount = contingencyPercent > 0 ? nonFlightTotal * (contingencyPercent / 100) : 0;
    total += contingencyAmount;

    // Admin fee — defaults to 5%, % excludes flights
    const excludeAdminFee = villa.excludeAdminFee === true;
    const adminFeePercent = !excludeAdminFee
        ? (villa.adminFeePercent !== undefined && villa.adminFeePercent !== null && villa.adminFeePercent !== '' && villa.adminFeePercent !== 0
            ? parseFloat(villa.adminFeePercent) : 5)
        : 0;
    const adminFeeFixedTotal = villa.adminFeeFixed ? parseFloat(villa.adminFeeFixed) : 0;
    const adminFeeFixedPP = villa.adminFeeFixedPP ? parseFloat(villa.adminFeeFixedPP) : 0;
    if (!excludeAdminFee) {
        if (adminFeePercent > 0) {
            total += (nonFlightTotal + contingencyAmount) * (adminFeePercent / 100);
        } else if (adminFeeFixedPP > 0) {
            total += adminFeeFixedPP;
        } else if (adminFeeFixedTotal > 0) {
            total += adminFeeFixedTotal / attendees;
        }
    }

    // Planning fee — defaults to 5%, % excludes flights
    const excludePlanningFee = villa.excludePlanningFee === true;
    const planningFeePercent = !excludePlanningFee
        ? (villa.planningFeePercent !== undefined && villa.planningFeePercent !== null && villa.planningFeePercent !== '' && villa.planningFeePercent !== 0
            ? parseFloat(villa.planningFeePercent) : 5)
        : 0;
    const planningFeeFixedTotal = villa.planningFeeFixed ? parseFloat(villa.planningFeeFixed) : 0;
    const planningFeeFixedPP = villa.planningFeeFixedPP ? parseFloat(villa.planningFeeFixedPP) : 0;
    if (!excludePlanningFee) {
        if (planningFeePercent > 0) {
            total += (nonFlightTotal + contingencyAmount) * (planningFeePercent / 100);
        } else if (planningFeeFixedPP > 0) {
            total += planningFeeFixedPP;
        } else if (planningFeeFixedTotal > 0) {
            total += planningFeeFixedTotal / attendees;
        }
    }

    // Add extra fees
    const extraFees = villa.extraFees || [];
    extraFees.forEach(fee => {
        total += (parseFloat(fee.amount) || 0) / attendees;
    });

    return total;
};

const LoadingText = styled.p`
    color: ${RebelColors.textMuted};
    text-align: center;
    padding: 2rem;
`;

const PropertyHeader = styled.div`
    margin-bottom: 1rem;
`;

const PropertyName = styled.h3`
    color: ${RebelColors.gold};
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 0.25rem;
`;

const PropertyLocation = styled.p`
    color: ${RebelColors.textMuted};
    font-size: 0.9rem;
    margin-bottom: 0;
`;

const VillaDetailsContainer = styled.div`
    background: linear-gradient(135deg, #000000 0%, #1f2937 50%, #374151 100%);
    border: 1px solid rgba(253, 181, 6, 0.3);
    border-radius: 12px;
    padding: 20px;
    margin: 16px 0;
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.2);
`;

const VillaDetail = styled.div`
    color: ${RebelColors.text};
    font-size: 14px;
    padding: 6px 14px;
    background: linear-gradient(135deg, rgba(253, 181, 6, 0.1) 0%, rgba(253, 181, 6, 0.05) 50%, transparent 100%);
    border: 1px solid rgba(253, 181, 6, 0.2);
    border-radius: 6px;
    display: flex;
    align-items: center;

    strong {
        color: ${RebelColors.gold};
        margin-right: 4px;
    }
`;

const ViewPropertyButton = styled.a`
    display: inline-flex;
    align-items: center;
    padding: 8px 20px;
    background: linear-gradient(90deg, #fdb506 0%, #f59e0b 100%);
    border: 2px solid #fdb506;
    border-radius: 6px;
    color: #000000 !important;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
    transition: all 0.3s ease;
    cursor: pointer;

    &:hover, &:visited, &:active, &:focus {
        transform: scale(1.02);
        box-shadow: 0 4px 12px rgba(253, 181, 6, 0.4);
        color: #000000 !important;
        text-decoration: none;
    }
`;

const NotesContainer = styled.div`
    background: linear-gradient(135deg, rgba(0, 0, 0, 0.6) 0%, rgba(31, 41, 55, 0.6) 50%, rgba(55, 65, 81, 0.6) 100%);
    border: 1px solid rgba(253, 181, 6, 0.2);
    border-radius: 10px;
    padding: 16px 20px;
    margin: 12px 0;
`;

const NotesLabel = styled.p`
    color: ${RebelColors.gold};
    font-weight: 600;
    font-size: 0.85rem;
    margin: 0 0 6px 0;
    display: flex;
    align-items: center;
`;

const NotesText = styled.p`
    color: ${RebelColors.text};
    font-size: 0.9rem;
    margin: 0;
    line-height: 1.5;
    white-space: pre-wrap;
`;

// ===== Alerts Section (#15-16) =====

const AlertsSection = styled.div`
    background: linear-gradient(135deg, rgba(239, 68, 68, 0.08) 0%, rgba(251, 191, 36, 0.06) 50%, rgba(0, 0, 0, 0.4) 100%);
    border: 2px solid rgba(239, 68, 68, 0.4);
    border-radius: 12px;
    padding: 16px 20px;
    margin: 16px 0;
`;

const AlertsSectionHeader = styled.h4`
    color: #ef4444;
    font-size: 1rem;
    font-weight: 700;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
`;

const AlertItem = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 10px 14px;
    margin-bottom: 8px;
    border-radius: 8px;
    background: ${props => {
        if (props.$type === 'blackout') return 'rgba(239, 68, 68, 0.12)';
        if (props.$type === 'callToBook') return 'rgba(251, 191, 36, 0.12)';
        return 'rgba(251, 191, 36, 0.08)';
    }};
    border-left: 4px solid ${props => {
        if (props.$type === 'blackout') return '#ef4444';
        if (props.$type === 'callToBook') return '#fbbf24';
        return '#fbbf24';
    }};

    &:last-child {
        margin-bottom: 0;
    }
`;

const AlertIcon = styled.div`
    color: ${props => props.$type === 'blackout' ? '#ef4444' : '#fbbf24'};
    font-size: 18px;
    margin-top: 2px;
    flex-shrink: 0;
`;

const AlertContent = styled.div`
    flex: 1;
`;

const AlertTitle = styled.p`
    color: ${props => props.$type === 'blackout' ? '#ef4444' : '#fbbf24'};
    font-weight: 700;
    font-size: 0.9rem;
    margin: 0 0 2px 0;
`;

const AlertMessage = styled.p`
    color: ${RebelColors.text};
    font-size: 0.85rem;
    margin: 0;
`;

const AlertReason = styled.div`
    display: flex;
    align-items: center;
    color: ${props => props.$type === 'blackout' ? '#fca5a5' : RebelColors.gold};
    background: ${props => props.$type === 'blackout' ? 'rgba(239, 68, 68, 0.08)' : 'rgba(253, 181, 6, 0.08)'};
    border: 1px solid ${props => props.$type === 'blackout' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(253, 181, 6, 0.2)'};
    border-radius: 6px;
    font-size: 0.85rem;
    font-weight: 500;
    padding: 8px 12px;
    margin: 8px 0 0 0;
`;

const DiscountBadge = styled.div`
    background: rgba(16, 185, 129, 0.15);
    border: 1px solid #10b981;
    color: #10b981;
    padding: 8px 14px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    margin: 12px 0;
    display: inline-flex;
    align-items: center;
`;

// ===== Summary header =====

const SummaryHeader = styled.div`
    display: flex;
    gap: 24px;
    margin: 16px 0;
    flex-wrap: wrap;
`;

const SummaryItem = styled.div`
    display: flex;
    align-items: baseline;
    gap: 8px;
`;

const SummaryLabel = styled.span`
    color: ${RebelColors.text};
    font-size: 0.95rem;
`;

const SummaryValue = styled.span`
    color: ${RebelColors.gold};
    font-size: 1.15rem;
    font-weight: 700;
`;

// ===== Trip Breakdown table =====

const TableTitle = styled.h4`
    color: ${RebelColors.gold};
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 8px;
`;

const CostTableContainer = styled.div`
    background: linear-gradient(135deg, rgba(0, 0, 0, 0.85) 0%, rgba(31, 41, 55, 0.85) 50%, rgba(55, 65, 81, 0.85) 100%);
    border: 1px solid rgba(253, 181, 6, 0.3);
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.2);
`;

const HeaderRow = styled(Row)`
    border-bottom: 2px solid rgba(253, 181, 6, 0.4);
    padding-bottom: 8px;
    margin-bottom: 4px;
`;

const CostRow = styled(Row)`
    padding: 10px 0;
    border-bottom: 1px solid rgba(253, 181, 6, 0.15);
    display: flex;
    align-items: center;

    &:last-child {
        border-bottom: none;
    }
`;

// ===== Category group (shared by Meals, Transportation, Activities) =====

const GroupHeader = styled(Row)`
    padding: 10px 0;
    border-bottom: 1px solid rgba(253, 181, 6, 0.15);
    display: flex;
    align-items: center;
    background: rgba(253, 181, 6, 0.04);
    border-radius: 6px 6px 0 0;
    margin-top: 2px;
    cursor: pointer;
`;

const GroupLabel = styled.p`
    color: ${RebelColors.gold};
    margin: 0;
    font-size: 0.9rem;
    font-weight: 700;
    letter-spacing: 0.3px;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const GroupToggle = styled.span`
    color: ${RebelColors.textMuted};
    font-size: 0.75rem;
    font-weight: 400;
    text-decoration: underline;
    transition: opacity 0.2s ease;

    &:hover {
        opacity: 0.8;
        color: ${RebelColors.gold};
    }
`;

const GroupSubRow = styled(Row)`
    padding: 6px 0;
    border-bottom: 1px solid rgba(253, 181, 6, 0.08);
    display: flex;
    align-items: center;
    background: rgba(253, 181, 6, 0.02);

    &:last-child {
        border-bottom: 1px solid rgba(253, 181, 6, 0.15);
        border-radius: 0 0 6px 6px;
    }
`;

const MealIndentCol = styled(Col)`
    display: flex;
    align-items: center;
    justify-content: center;
`;

const MealSubName = styled.p`
    color: ${RebelColors.text};
    margin: 0;
    font-size: 0.82rem;
    padding-left: 12px;
    opacity: 0.9;
`;

const MealSubDescription = styled.p`
    color: ${RebelColors.textMuted};
    margin: 0;
    font-size: 0.75rem;
    padding-left: 12px;
`;

const MealSubAmount = styled.p`
    color: ${RebelColors.text};
    margin: 0;
    font-size: 0.82rem;
    opacity: 0.9;
`;

const IconCol = styled(Col)`
    display: flex;
    align-items: center;
    justify-content: center;
`;

const CategoryIcon = styled(FontAwesomeIcon)`
    color: ${RebelColors.gold};
    font-size: 16px;
    width: 20px !important;
`;

const CategoryName = styled.p`
    color: ${RebelColors.text};
    margin: 0;
    font-size: 0.9rem;
    font-weight: 600;
`;

const CostDescription = styled.p`
    color: ${RebelColors.textMuted};
    margin: 0;
    font-size: 0.8rem;
`;

const CostAmount = styled.p`
    color: ${RebelColors.text};
    margin: 0;
    font-size: 0.9rem;
`;

const MultiplierCol = styled(Col)`
    display: flex;
    align-items: center;
    justify-content: center;
`;

const MultiplierText = styled.p`
    color: ${RebelColors.textMuted};
    margin: 0;
    font-size: 0.85rem;
`;

const SeparatorRow = styled(Row)`
    padding: 0;
`;

const SeparatorLine = styled.div`
    background-color: rgba(253, 181, 6, 0.4);
    height: 1px;
    margin: 8px 0;
    border-radius: 1px;
`;

const GrandSeparatorLine = styled.div`
    background-color: ${RebelColors.gold};
    height: 2px;
    margin: 8px 0;
    border-radius: 1px;
`;

// Subtotal row
const SubtotalRow = styled(Row)`
    padding: 10px 0;
    display: flex;
    align-items: center;
    border-bottom: 1px solid rgba(253, 181, 6, 0.15);
`;

const SubtotalLabel = styled.p`
    color: ${RebelColors.text};
    font-weight: 600;
    margin: 0;
    font-size: 0.95rem;
`;

const SubtotalAmount = styled.p`
    color: ${RebelColors.text};
    font-weight: 600;
    margin: 0;
    font-size: 0.95rem;
`;

// Fee rows (#3)
const FeeRow = styled(Row)`
    padding: 8px 0;
    border-bottom: 1px solid rgba(253, 181, 6, 0.1);
    display: flex;
    align-items: center;
`;

const FeeIcon = styled(FontAwesomeIcon)`
    color: ${RebelColors.gold};
    font-size: 14px;
    width: 20px !important;
`;

const FeeName = styled.p`
    color: ${RebelColors.textMuted};
    margin: 0;
    font-size: 0.85rem;
    font-style: italic;
`;

const FeeAmount = styled.p`
    color: ${RebelColors.textMuted};
    margin: 0;
    font-size: 0.85rem;
`;

const TotalRow = styled(Row)`
    padding: 10px 0;
    display: flex;
    align-items: center;
`;

const TotalIcon = styled(FontAwesomeIcon)`
    color: ${RebelColors.gold};
    font-size: 16px;
    width: 20px !important;
`;

const TotalLabel = styled.p`
    color: ${RebelColors.gold};
    font-weight: 700;
    margin: 0;
    font-size: 1rem;
`;

const TotalAmount = styled.p`
    color: ${RebelColors.gold};
    font-weight: 700;
    margin: 0;
    font-size: 1rem;
`;

const EmptyRow = styled(Row)`
    padding: 1rem 0;
`;

const EmptyText = styled.p`
    color: ${RebelColors.textMuted};
    text-align: center;
    font-style: italic;
    margin: 0;
`;

const BoldLabel = styled.p`
    color: ${RebelColors.gold};
    font-weight: 600;
    margin-bottom: 0;
    font-size: 0.9rem;
`;

const RemoveRow = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 1.5rem;
`;

const BreakdownToggle = styled.span`
    color: ${RebelColors.gold};
    cursor: pointer;
    margin-left: 8px;
    font-size: 0.75rem;
    text-decoration: underline;
    transition: opacity 0.2s ease;

    &:hover {
        opacity: 0.8;
    }
`;

const NightlyBreakdownContainer = styled.div`
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(253, 181, 6, 0.15);
    border-radius: 8px;
    padding: 10px 16px;
    margin: 4px 0 8px 40px;
`;

const NightlyRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 4px 0;
    border-bottom: 1px solid rgba(253, 181, 6, 0.08);
    background: ${props => {
        if (props.$isBlackout) return 'rgba(239, 68, 68, 0.08)';
        if (props.$isCallToBook) return 'rgba(251, 191, 36, 0.06)';
        return 'transparent';
    }};
    padding-left: ${props => (props.$isBlackout || props.$isCallToBook) ? '8px' : '0'};
    border-radius: ${props => (props.$isBlackout || props.$isCallToBook) ? '4px' : '0'};

    &:last-child {
        border-bottom: none;
    }
`;

const NightDate = styled.span`
    color: ${RebelColors.textMuted};
    font-size: 0.8rem;
    min-width: 100px;
`;

const NightRate = styled.span`
    color: ${props => props.$isOverride ? RebelColors.gold : RebelColors.text};
    font-size: 0.8rem;
    font-weight: ${props => props.$isOverride ? '600' : '400'};
    display: flex;
    align-items: center;
    gap: 6px;
`;

const OverrideIndicator = styled.span`
    background: rgba(253, 181, 6, 0.2);
    color: ${RebelColors.gold};
    font-size: 0.65rem;
    padding: 1px 5px;
    border-radius: 3px;
    font-weight: 500;
`;

const NightFlag = styled.span`
    font-size: 0.7rem;
    padding: 2px 6px;
    border-radius: 3px;
    font-weight: 500;
    background: ${props => props.$type === 'blackout' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(251, 191, 36, 0.15)'};
    color: ${props => props.$type === 'blackout' ? '#ef4444' : '#fbbf24'};
    border: 1px solid ${props => props.$type === 'blackout' ? '#ef4444' : '#fbbf24'};
`;

const NightNote = styled.span`
    color: ${RebelColors.textMuted};
    font-size: 0.7rem;
    font-style: italic;
`;
